import { dollars, pct } from "../lib/format";
import type { ExitType, ThesisRow } from "../lib/types";

type Props = {
  theses: ThesisRow[];
};

const EXIT_TYPE_LABELS: Record<ExitType, { label: string; className: string }> = {
  stop: { label: "stop", className: "bg-rose-900/40 text-rose-300" },
  take_profit: { label: "take profit", className: "bg-emerald-900/40 text-emerald-300" },
  session_end: { label: "session end", className: "bg-slate-700/40 text-slate-300" },
  time_limit: { label: "time limit", className: "bg-amber-900/40 text-amber-300" },
  manual: { label: "manual", className: "bg-sky-900/40 text-sky-300" },
  signal_invalidated: {
    label: "thesis broke",
    className: "bg-purple-900/40 text-purple-300",
  },
};

/**
 * Closed theses with mechanical exit-type breakdown.
 *
 * Shows the operator WHY each trade ended (stopped / TP / forced /
 * manual), not just what the P&L was. Across enough samples this lets
 * Friday review answer "are our stops set too tight?" vs "are our TPs
 * too far?" — the bot's hardest tuning question.
 *
 * Hides itself entirely when there are no CLOSED theses yet for the
 * strategy.
 */
export function ClosedThesesPanel({ theses }: Props) {
  if (theses.length === 0) {
    return null;
  }

  // Count by exit_type for the strip-chart summary.
  const counts: Partial<Record<ExitType | "unknown", number>> = {};
  for (const t of theses) {
    const key = t.exit_type ?? "unknown";
    counts[key] = (counts[key] ?? 0) + 1;
  }
  const total = theses.length;

  return (
    <section className="rounded-lg border border-ink-700 bg-ink-900">
      <div className="border-b border-ink-700 px-3 py-2 text-sm font-semibold text-slate-200">
        Closed theses
        <span className="ml-2 text-xs font-normal text-slate-500">
          ({total})
        </span>
      </div>

      {/* Exit-type strip — bars sized by count. */}
      <div className="border-b border-ink-700 px-3 py-2">
        <div className="mb-1 text-xs uppercase tracking-wider text-slate-500">
          Exit reasons
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {Object.entries(counts).map(([key, n]) => {
            const meta =
              key === "unknown"
                ? {
                    label: "unrecorded",
                    className: "bg-slate-800 text-slate-400",
                  }
                : EXIT_TYPE_LABELS[key as ExitType];
            return (
              <span
                key={key}
                className={`rounded px-1.5 py-0.5 ${meta.className}`}
              >
                {meta.label}: {n}
              </span>
            );
          })}
        </div>
      </div>

      <ul>
        {theses.slice(0, 30).map((t) => {
          const pnlPositive =
            t.closed_at_pnl_pct != null && t.closed_at_pnl_pct >= 0;
          const pnlClass =
            t.closed_at_pnl_pct == null
              ? "text-slate-500"
              : pnlPositive
                ? "text-accent-ok"
                : "text-accent-err";
          const meta = t.exit_type
            ? EXIT_TYPE_LABELS[t.exit_type]
            : { label: "unrecorded", className: "bg-slate-800 text-slate-400" };
          return (
            <li
              key={`${t.thesis_id}-${t.timestamp_utc}`}
              className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-ink-700/50 px-3 py-2 text-sm last:border-b-0"
            >
              <div className="flex items-baseline gap-2">
                <span className="font-mono font-medium text-slate-100">
                  {t.ticker}
                </span>
                {t.is_live === 1 && (
                  <span
                    className="rounded bg-amber-900/60 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-200"
                    title="Real-money trade"
                  >
                    💸 LIVE
                  </span>
                )}
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${meta.className}`}
                >
                  {meta.label}
                </span>
                {t.name && (
                  <span className="text-xs text-slate-400">{t.name}</span>
                )}
              </div>
              <div className="flex items-baseline gap-3 font-mono text-xs">
                {t.fill_price_cents != null && t.exit_price_cents != null && (
                  <span className="text-slate-500">
                    {dollars(t.fill_price_cents)} →{" "}
                    {dollars(t.exit_price_cents)}
                  </span>
                )}
                <span className={pnlClass}>{pct(t.closed_at_pnl_pct)}</span>
                <span className="text-slate-600">
                  {t.trading_day_et}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {theses.length > 30 && (
        <div className="border-t border-ink-700 px-3 py-2 text-center text-xs text-slate-500">
          showing 30 most recent of {theses.length}
        </div>
      )}
    </section>
  );
}
